/* =============================================================================
   SLOAT core
   One module, loaded on every page. No dependencies.

   - Dialogs: drawers, sheets, search, menu. Animated in and out, focus kept,
     scroll locked, Esc and scrim close, swipe down to dismiss on phones.
   - Header: hide on scroll down, show on scroll up, transparent-to-solid over the
     hero, desktop flyout with hover intent, mobile drill-down menu.
   - Search: predictive results via the Section Rendering API, cached, keyboard nav.
   - Cart: add / change through the AJAX API with bundled section rendering,
     drawer swaps in place, count pops, new line highlights, toast for quick add.
   - Product cards: live swatches, desktop quick add, phone quick-add sheet.
   - VariantForm: shared option logic for the product page and quick add.
   - Motion: reveal on scroll, image fade-in, shared-element page transition.
   ========================================================================== */

const Sloat = (window.Sloat = window.Sloat || {});
const doc = document;
const root = doc.documentElement;
const $ = (sel, scope = doc) => scope.querySelector(sel);
const $$ = (sel, scope = doc) => Array.from(scope.querySelectorAll(sel));
const mq = (q) => window.matchMedia(q).matches;
const isPhone = () => mq('(max-width: 749px)');
const isTouch = () => mq('(hover: none)');
const reducedMotion = () => mq('(prefers-reduced-motion: reduce)') || root.classList.contains('sloat-motion-reduced');
const nextFrame = () => new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
const wait = (ms) => new Promise((r) => setTimeout(r, ms));
/* Same rule as snippets/sloat-title.liquid: ALL CAPS titles become Title Case, anything else is left alone. */
const titleCase = (t = '') => (t === t.toUpperCase() ? t.toLowerCase().replace(/(^|[\s-])(\S)/g, (m, sep, ch) => sep + ch.toUpperCase()) : t);
const routesRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

Sloat.$ = $;
Sloat.$$ = $$;

/* ---------- Money ---------- */
Sloat.money = (cents) => {
  const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
  const value = Number(cents) / 100;
  try {
    return new Intl.NumberFormat(root.lang || 'en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: Number(cents) % 100 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch (e) {
    return `$${value}`;
  }
};

/* ---------- HTML helpers ---------- */
const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');
Sloat.fetchSection = async (url, sectionId, signal) => {
  const u = new URL(url, location.origin);
  u.searchParams.set('section_id', sectionId);
  const res = await fetch(u.toString(), { signal, credentials: 'same-origin' });
  if (!res.ok) throw new Error(`Section ${sectionId} failed: ${res.status}`);
  return res.text();
};

/* =============================================================================
   Dialogs
   ========================================================================== */
const Dialogs = {
  stack: [],
  triggers: new WeakMap(),

  get(name) {
    if (typeof name !== 'string') return name;
    // "filter-*" opens the first filter dialog on the page (header icons sit outside the results section).
    if (name.endsWith('*')) return $(`[data-sloat-dialog^="${name.slice(0, -1)}"]`);
    return $(`[data-sloat-dialog="${name}"]`);
  },

  async open(name, { trigger } = {}) {
    const dialog = this.get(name);
    if (!dialog) return null;
    if (dialog.open && dialog.dataset.state === 'open') return dialog;

    // One overlay at a time, except a sheet opened over a drawer.
    for (const other of [...this.stack]) {
      if (other !== dialog) this.close(other, { instant: true, keepLock: true });
    }

    if (trigger) this.triggers.set(dialog, trigger);
    dialog.dispatchEvent(new CustomEvent('sloat:before-open', { bubbles: true }));
    if (!dialog.open) {
      try { dialog.showModal(); } catch (e) { dialog.setAttribute('open', ''); }
    }
    this.stack = this.stack.filter((d) => d !== dialog).concat(dialog);
    this.lock();
    dialog.dataset.state = '';
    await nextFrame();
    dialog.dataset.state = 'open';
    const focusTarget = $('[autofocus], [data-autofocus]', dialog);
    if (focusTarget) focusTarget.focus({ preventScroll: true });
    else $('[data-panel]', dialog)?.focus?.({ preventScroll: true });
    dialog.dispatchEvent(new CustomEvent('sloat:open', { bubbles: true }));
    return dialog;
  },

  close(name, { instant = false, keepLock = false } = {}) {
    const dialog = this.get(name);
    if (!dialog || !dialog.open || dialog.dataset.state === 'closing') return Promise.resolve();
    dialog.dataset.state = 'closing';
    this.stack = this.stack.filter((d) => d !== dialog);
    const panel = $('[data-panel]', dialog);
    if (panel) panel.style.transform = '';
    return new Promise((resolve) => {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        dialog.close();
        delete dialog.dataset.state;
        if (!keepLock && !this.stack.length) this.unlock();
        const trigger = this.triggers.get(dialog);
        if (trigger && doc.contains(trigger) && !instant) trigger.focus({ preventScroll: true });
        dialog.dispatchEvent(new CustomEvent('sloat:close', { bubbles: true }));
        resolve();
      };
      if (instant || reducedMotion() || !panel) return finish();
      panel.addEventListener('transitionend', (e) => e.target === panel && finish(), { once: false });
      setTimeout(finish, 460);
    });
  },

  closeAll() {
    [...this.stack].forEach((d) => this.close(d));
  },

  lock() {
    // Keep the page from shifting when the scrollbar disappears.
    if (root.classList.contains('sloat-locked')) return;
    const bar = window.innerWidth - root.clientWidth;
    root.style.setProperty('--sloat-scrollbar', `${Math.max(0, bar)}px`);
    root.classList.add('sloat-locked');
  },
  unlock() {
    root.classList.remove('sloat-locked');
  },

  init() {
    doc.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-sloat-open]');
      if (opener) {
        const name = opener.getAttribute('data-sloat-open');
        if (this.get(name)) {
          e.preventDefault();
          this.open(name, { trigger: opener });
        }
        return;
      }
      const closer = e.target.closest('[data-sloat-close]');
      if (closer) {
        const dialog = closer.closest('dialog');
        if (dialog) {
          e.preventDefault();
          this.close(dialog);
        }
        return;
      }
      // Click on the bare dialog box (outside the panel) closes it.
      if (e.target.matches && e.target.matches('dialog.sloat-dialog')) this.close(e.target);
    });

    // Esc always closes the top overlay, even from a search field that would
    // otherwise swallow the first press to clear itself.
    doc.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape' || !this.stack.length) return;
      e.preventDefault();
      this.close(this.stack[this.stack.length - 1]);
    });

    doc.addEventListener(
      'cancel',
      (e) => {
        if (e.target.matches && e.target.matches('dialog.sloat-dialog')) {
          e.preventDefault();
          this.close(e.target);
        }
      },
      true
    );

    this.initSwipe();
  },

  /* Swipe down to dismiss bottom sheets on phones. Starts on the sheet head or
     anywhere when the body is scrolled to the top. */
  initSwipe() {
    let drag = null;
    doc.addEventListener(
      'pointerdown',
      (e) => {
        if (e.pointerType !== 'touch') return;
        const dialog = e.target.closest('dialog.sloat-dialog');
        if (!dialog || dialog.dataset.state !== 'open') return;
        const isSheet = dialog.classList.contains('sloat-dialog--bottom') || (dialog.classList.contains('sloat-dialog--sheet-mobile') && isPhone());
        if (!isSheet) return;
        const panel = $('[data-panel]', dialog);
        const scroller = e.target.closest('.sloat-drawer__body');
        if (scroller && scroller.scrollTop > 0) return;
        if (e.target.closest('input, textarea, select, .sloat-chips-row, .sloat-swatches')) return;
        drag = { dialog, panel, y0: e.clientY, t0: performance.now(), dy: 0 };
      },
      { passive: true }
    );
    doc.addEventListener(
      'pointermove',
      (e) => {
        if (!drag) return;
        const dy = Math.max(0, e.clientY - drag.y0);
        if (dy > 4) drag.dialog.classList.add('is-dragging');
        drag.dy = dy;
        drag.panel.style.transform = `translate3d(0, ${dy}px, 0)`;
      },
      { passive: true }
    );
    const end = () => {
      if (!drag) return;
      const { dialog, panel, dy, t0 } = drag;
      const velocity = dy / Math.max(1, performance.now() - t0);
      dialog.classList.remove('is-dragging');
      if (dy > 96 || (dy > 24 && velocity > 0.6)) {
        panel.style.transform = '';
        this.close(dialog);
      } else {
        panel.style.transform = '';
      }
      drag = null;
    };
    doc.addEventListener('pointerup', end, { passive: true });
    doc.addEventListener('pointercancel', end, { passive: true });
  },
};
Sloat.dialogs = Dialogs;

/* =============================================================================
   Header
   ========================================================================== */
const Header = {
  init() {
    this.wrap = $('[data-sloat-header]');
    if (!this.wrap) return;
    this.bar = $('[data-header-bar]', this.wrap);
    this.trigger = $('[data-flyout-trigger]', this.wrap);
    this.flyout = $('[data-flyout]', this.wrap);
    this.transparent = this.wrap.dataset.transparent === 'true';
    this.sticky = this.wrap.dataset.sticky;
    this.lastY = window.scrollY;
    this.ticking = false;

    window.addEventListener('scroll', () => this.onScroll(), { passive: true });
    window.addEventListener('resize', () => this.measure(), { passive: true });
    this.measure();
    this.onScroll(true);

    // Solid while the pointer is over a transparent header.
    this.wrap.addEventListener('pointerenter', (e) => e.pointerType === 'mouse' && this.engage(true));
    this.wrap.addEventListener('pointerleave', (e) => {
      if (e.pointerType !== 'mouse') return;
      if (this.scheduleClose) this.scheduleClose();
      if (!this.flyoutOpen) this.engage(false);
    });

    if (this.trigger && this.flyout) this.initFlyout();
    this.initMenu();
  },

  measure() {
    this.firstSection = $('#MainContent > .shopify-section');
    this.threshold = this.transparent && this.firstSection ? Math.max(40, this.firstSection.offsetHeight - this.bar.offsetHeight - 40) : 0;
    this.updateVisibleBottom();
  },

  updateVisibleBottom() {
    const r = this.bar.getBoundingClientRect();
    const bottom = this.wrap.classList.contains('is-hidden') ? 0 : Math.max(0, r.bottom);
    root.style.setProperty('--sloat-header-visible', `${Math.round(bottom)}px`);
  },

  engage(on) {
    this.wrap.classList.toggle('is-engaged', on || this.flyoutOpen || (this.transparent && window.scrollY > this.threshold));
  },

  onScroll(force) {
    if (this.ticking && !force) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      const y = window.scrollY;
      const dy = y - this.lastY;
      if (this.transparent) {
        this.wrap.dataset.state = y > this.threshold ? 'solid' : 'transparent';
      }
      if (this.sticky === 'scroll-up' && !this.flyoutOpen && !Dialogs.stack.length) {
        if (y > 320 && dy > 6) this.wrap.classList.add('is-hidden');
        else if (dy < -6 || y < 120) this.wrap.classList.remove('is-hidden');
      }
      this.lastY = y;
      this.updateVisibleBottom();
      this.ticking = false;
    });
  },

  initFlyout() {
    let openTimer;
    this.flyoutOpen = false;
    const open = () => {
      clearTimeout(this.closeTimer);
      if (this.flyoutOpen) return;
      this.flyoutOpen = true;
      this.flyout.classList.add('is-open');
      this.flyout.setAttribute('aria-hidden', 'false');
      this.trigger.setAttribute('aria-expanded', 'true');
      this.wrap.classList.add('is-flyout', 'is-engaged');
    };
    const close = () => {
      clearTimeout(openTimer);
      if (!this.flyoutOpen) return;
      this.flyoutOpen = false;
      this.flyout.classList.remove('is-open');
      this.flyout.setAttribute('aria-hidden', 'true');
      this.trigger.setAttribute('aria-expanded', 'false');
      this.wrap.classList.remove('is-flyout');
      this.engage(false);
    };
    this.openFlyout = open;
    this.closeFlyout = close;
    this.scheduleClose = () => {
      clearTimeout(openTimer);
      clearTimeout(this.closeTimer);
      this.closeTimer = setTimeout(close, 220);
    };

    this.trigger.addEventListener('pointerenter', (e) => {
      if (e.pointerType !== 'mouse') return;
      clearTimeout(openTimer);
      openTimer = setTimeout(open, this.flyoutOpen ? 0 : 110);
    });
    this.flyout.addEventListener('pointerenter', () => clearTimeout(this.closeTimer));
    $$('.sloat-header__link:not([data-flyout-trigger])', this.wrap).forEach((link) =>
      link.addEventListener('pointerenter', (e) => e.pointerType === 'mouse' && this.scheduleClose())
    );
    $$('.sloat-header__end > *, .sloat-header__logo', this.wrap).forEach((el) =>
      el.addEventListener('pointerenter', (e) => e.pointerType === 'mouse' && this.scheduleClose())
    );

    // Touch and keyboard: first activation opens, second follows the link.
    this.trigger.addEventListener('click', (e) => {
      if (!this.flyoutOpen) {
        e.preventDefault();
        open();
        $('a', this.flyout)?.focus({ preventScroll: true });
      }
    });
    this.trigger.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        open();
        $('a', this.flyout)?.focus({ preventScroll: true });
      }
    });
    this.wrap.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.flyoutOpen) {
        close();
        this.trigger.focus();
      }
    });
    this.wrap.addEventListener('focusout', (e) => {
      if (!this.wrap.contains(e.relatedTarget)) close();
    });
    const scrim = $('[data-flyout-scrim]', this.wrap);
    scrim?.addEventListener('click', close);
    scrim?.addEventListener('pointerenter', (e) => e.pointerType === 'mouse' && this.scheduleClose());
  },

  initMenu() {
    const dialog = $('[data-sloat-dialog="menu"]');
    if (!dialog) return;
    const screens = () => $$('[data-menu-screen]', dialog);
    const show = (name, instant = false) => {
      if (instant) dialog.classList.add('is-instant');
      screens().forEach((s) => {
        const current = s.dataset.menuScreen === name;
        s.classList.toggle('is-current', current);
        s.toggleAttribute('inert', !current);
        if (s.dataset.menuScreen === 'root') s.classList.toggle('is-behind', name !== 'root');
      });
      const target = screens().find((s) => s.dataset.menuScreen === name);
      target?.scrollTo(0, 0);
      $$('[data-menu-go]', dialog).forEach((b) => b.setAttribute('aria-expanded', String(b.dataset.menuGo === name)));
      requestAnimationFrame(() => {
        $('a, button:not([data-sloat-close])', target)?.focus({ preventScroll: true });
        if (instant) requestAnimationFrame(() => dialog.classList.remove('is-instant'));
      });
    };
    dialog.addEventListener('click', (e) => {
      const go = e.target.closest('[data-menu-go]');
      if (go) return show(go.dataset.menuGo);
      if (e.target.closest('[data-menu-back]')) show('root');
    });
    // Collection pages open straight to the Shop categories; Back still reaches the root.
    const start = dialog.dataset.startScreen || 'root';
    dialog.addEventListener('sloat:before-open', () => show(start, true));
    dialog.addEventListener('sloat:close', () => show(start, true));
  },
};
Sloat.header = Header;

/* =============================================================================
   Search
   ========================================================================== */
const Search = {
  init() {
    this.dialog = $('[data-sloat-dialog="search"]');
    if (!this.dialog) return;
    this.input = $('[data-search-input]', this.dialog);
    this.idle = $('[data-search-idle]', this.dialog);
    this.live = $('[data-search-live]', this.dialog);
    this.cache = new Map();

    this.dialog.addEventListener('sloat:before-open', () => {
      const header = $('[data-header-bar]');
      const top = header && !isPhone() ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
      this.dialog.style.setProperty('--sloat-search-top', `${Math.round(top)}px`);
      Header.wrap?.classList.add('is-engaged');
    });
    this.dialog.addEventListener('sloat:close', () => Header.wrap && Header.engage(false));
    this.dialog.addEventListener('sloat:open', () => {
      this.input.focus({ preventScroll: true });
      this.input.select();
    });
    this.input.addEventListener('input', () => this.onInput());
    this.input.addEventListener('keydown', (e) => this.onKey(e));
    this.dialog.addEventListener('keydown', (e) => {
      if (e.target !== this.input && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) this.onKey(e);
    });
  },

  onInput() {
    clearTimeout(this.timer);
    const q = this.input.value.trim();
    if (!q) {
      this.controller?.abort();
      this.live.hidden = true;
      this.idle.hidden = false;
      return;
    }
    this.timer = setTimeout(() => this.query(q), this.cache.has(q) ? 0 : 140);
  },

  async query(q) {
    if (this.cache.has(q)) return this.render(this.cache.get(q));
    this.controller?.abort();
    this.controller = new AbortController();
    this.live.classList.add('is-loading');
    const url = new URL(`${routesRoot}search/suggest`, location.origin);
    url.searchParams.set('q', q);
    url.searchParams.set('resources[type]', 'product,query,collection,page');
    url.searchParams.set('resources[limit]', '4');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('section_id', 'sloat-predictive-search');
    try {
      const res = await fetch(url, { signal: this.controller.signal });
      const html = await res.text();
      const inner = $('.sloat-search__live-inner', parseHTML(html));
      if (!inner) return;
      this.cache.set(q, inner.outerHTML);
      if (this.input.value.trim() === q) this.render(inner.outerHTML);
    } catch (e) {
      if (e.name !== 'AbortError') console.warn('[sloat] search', e);
    } finally {
      this.live.classList.remove('is-loading');
    }
  },

  render(html) {
    this.live.innerHTML = html;
    this.idle.hidden = true;
    this.live.hidden = false;
    Sloat.media(this.live);
  },

  onKey(e) {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const items = $$('[data-search-item]', this.live.hidden ? this.idle : this.live).concat(this.live.hidden ? $$('[data-search-term]', this.idle) : []);
    const list = [...new Set(items)].filter((el) => el.offsetParent);
    if (!list.length) return;
    e.preventDefault();
    const i = list.indexOf(doc.activeElement);
    const next = e.key === 'ArrowDown' ? (i + 1) % list.length : i <= 0 ? -1 : i - 1;
    if (next === -1) return this.input.focus();
    list[next].focus();
  },
};

/* =============================================================================
   Cart
   ========================================================================== */
const Cart = {
  sectionId: 'sloat-cart-drawer',

  get drawer() {
    return $('[data-sloat-dialog="cart"]');
  },

  init() {
    // Quantity steppers and remove links inside the drawer and the cart page.
    doc.addEventListener('click', (e) => {
      const step = e.target.closest('[data-qty]');
      if (step && step.closest('[data-cart-contents]')) {
        e.preventDefault();
        const line = step.closest('[data-key]');
        const input = $('[data-qty-input]', line);
        const max = input.max ? Number(input.max) : Infinity;
        const qty = Math.min(max, Math.max(0, Number(input.value) + Number(step.dataset.qty)));
        if (qty === Number(input.value)) return line.querySelector('.sloat-stepper')?.classList.add('sloat-nudge');
        input.value = qty;
        this.change(line, qty);
        return;
      }
      const remove = e.target.closest('[data-remove]');
      if (remove && remove.closest('[data-cart-contents]')) {
        e.preventDefault();
        this.change(remove.closest('[data-key]'), 0);
      }
    });
    doc.addEventListener('change', (e) => {
      const input = e.target.closest('[data-qty-input]');
      if (input && input.closest('[data-cart-contents]')) this.change(input.closest('[data-key]'), Math.max(0, Number(input.value) || 0));
    });
    doc.addEventListener('animationend', (e) => e.target.classList?.remove('sloat-nudge'));

    // Any form marked data-sloat-add posts through AJAX.
    doc.addEventListener('submit', (e) => {
      const form = e.target.closest('form[data-sloat-add]');
      if (!form) return;
      e.preventDefault();
      this.submit(form, e.submitter);
    });

    // Stay in sync with anything else that changes the cart (Horizon blocks, apps).
    doc.addEventListener('cart:update', (e) => {
      if (e.detail && e.detail.sloat) return;
      this.refresh();
    });
    window.addEventListener('pageshow', (e) => e.persisted && this.refresh());
  },

  async submit(form, submitter) {
    const vf = form.sloatVariantForm;
    if (vf && !vf.validate()) return;
    const button = submitter && submitter.matches('[data-async], .sloat-size') ? submitter : $('[data-add-button], [data-async]', form);
    const fd = new FormData(form);
    if (submitter && submitter.name) fd.set(submitter.name, submitter.value);
    if (!fd.get('id')) return;

    const isQuick = form.hasAttribute('data-quick-form');
    const inDrawer = !!form.closest('[data-sloat-dialog="cart"]');
    const inSheet = form.closest('[data-sloat-dialog="quick"]');

    this.setButton(button, 'loading');
    if (submitter && submitter.classList.contains('sloat-size')) submitter.classList.add('is-adding');

    try {
      const { item, sections } = await this.add(fd);
      this.render(sections, { highlight: item && item.key });
      this.setButton(button, 'done');

      if (isQuick) {
        this.quickDone(form);
        this.toast(item);
      } else if (inSheet) {
        await wait(760);
        await Dialogs.close(inSheet, { keepLock: true });
        this.open();
      } else if (inDrawer) {
        // Drawer re-rendered in place.
      } else if (form.dataset.addBehavior === 'toast') {
        this.toast(item);
      } else {
        await wait(520);
        this.open();
      }
      setTimeout(() => this.setButton(button, 'idle'), 1800);
    } catch (err) {
      this.setButton(button, 'idle');
      submitter?.classList.remove('is-adding');
      this.toast(null, err.message);
    }
  },

  async add(formData) {
    formData.append('sections', this.sectionId);
    formData.append('sections_url', location.pathname);
    const res = await fetch(`${routesRoot}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || 'That size could not be added.');
    const item = data.items ? data.items[0] : data;
    return { item, sections: data.sections };
  },

  async addItems(items) {
    const res = await fetch(`${routesRoot}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections: [this.sectionId], sections_url: location.pathname }),
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || 'Could not add these pieces.');
    this.render(data.sections, { highlight: data.items && data.items.map((i) => i.key) });
    return data;
  },

  async change(line, quantity) {
    if (!line) return;
    const key = line.dataset.key;
    line.classList.add(quantity === 0 ? 'is-removing' : 'is-updating');
    try {
      const res = await fetch(`${routesRoot}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity, sections: [this.sectionId], sections_url: location.pathname }),
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || 'Could not update the cart.');
      if (quantity === 0) await wait(380);
      this.render(data.sections);
    } catch (err) {
      line.classList.remove('is-updating', 'is-removing');
      this.toast(null, err.message);
      this.refresh();
    }
  },

  async refresh() {
    try {
      const res = await fetch(`${routesRoot}?sections=${this.sectionId}`);
      this.render(await res.json());
    } catch (e) {
      /* offline: keep what we have */
    }
  },

  render(sections, { highlight } = {}) {
    const html = sections && sections[this.sectionId];
    if (!html) return;
    const fresh = $('[data-cart-contents]', parseHTML(html));
    if (!fresh) return;
    const keys = [].concat(highlight || []);
    $$('[data-cart-contents]').forEach((current) => {
      const context = current.dataset.context;
      const scroller = $('.sloat-drawer__body', current);
      const scrollTop = scroller ? scroller.scrollTop : 0;
      const node = fresh.cloneNode(true);
      if (context === 'page') {
        // The cart page renders its own heading level and has no close button.
        node.dataset.context = 'page';
        $('[data-sloat-close]', node)?.remove();
      }
      current.replaceWith(node);
      const body = $('.sloat-drawer__body', node);
      if (body) body.scrollTop = scrollTop;
      keys.forEach((k) => $(`[data-key="${CSS.escape(k)}"]`, node)?.classList.add('is-new'));
      Sloat.media(node);
    });
    const total = $('[data-cart-total]', this.drawer || doc);
    if (total) total.classList.add('sloat-cart__total-roll');
    this.updateCount(Number(fresh.dataset.cartCount));
    doc.dispatchEvent(new CustomEvent('sloat:cart', { detail: { count: Number(fresh.dataset.cartCount) } }));
  },

  updateCount(count) {
    $$('[data-cart-count]').forEach((el) => {
      const prev = Number(el.dataset.count);
      el.dataset.count = String(count);
      const num = $('.sloat-count__num', el);
      if (num) num.textContent = count;
      if (count > prev && !reducedMotion()) {
        el.classList.remove('is-bumping');
        void el.offsetWidth;
        el.classList.add('is-bumping');
      }
    });
    $$('[data-cart-link]').forEach((a) => a.setAttribute('aria-label', `Cart, ${count} ${count === 1 ? 'item' : 'items'}`));
  },

  open() {
    const header = $('[data-sloat-header]');
    header?.classList.remove('is-hidden');
    return Dialogs.open('cart', { trigger: $('[data-cart-link]') });
  },

  setButton(button, state) {
    if (!button) return;
    if (state === 'idle') {
      delete button.dataset.state;
      button.classList.remove('is-adding');
      button.removeAttribute('aria-busy');
      return;
    }
    button.dataset.state = state;
    button.setAttribute('aria-busy', String(state === 'loading'));
  },

  quickDone(form) {
    const details = form.closest('[data-quick]');
    if (!details) return;
    const panel = $('.sloat-quick__panel', details);
    const done = doc.createElement('div');
    done.className = 'sloat-quick__done';
    done.innerHTML = '<svg class="sloat-icon" width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path class="sloat-icon__check" d="m4 10.5 4 4 8-9"/></svg><span>Added</span>';
    form.hidden = true;
    panel.appendChild(done);
    setTimeout(() => {
      Cards.closeQuick(details).then(() => {
        form.hidden = false;
        done.remove();
        $$('.is-adding', form).forEach((b) => b.classList.remove('is-adding'));
      });
    }, 1200);
  },

  toast(item, error) {
    const el = $('[data-sloat-toast]');
    if (!el) return;
    clearTimeout(this.toastTimer);
    if (error) {
      el.innerHTML = `<div class="sloat-toast__text"><span class="sloat-label">Not added</span><span class="sloat-body-sm">${escapeHTML(error)}</span></div>`;
    } else if (item) {
      const img = item.image ? `${item.image}${item.image.includes('?') ? '&' : '?'}width=160` : '';
      const variant = item.variant_title && !/default title/i.test(item.variant_title) ? `<span class="sloat-caption sloat-muted">${escapeHTML(item.variant_title)}</span>` : '';
      el.innerHTML = `
        ${img ? `<span class="sloat-media sloat-toast__media"><img src="${img}" alt="" width="48" height="60"></span>` : ''}
        <div class="sloat-toast__text"><span class="sloat-label sloat-muted">Added to cart</span><span class="sloat-body-sm">${escapeHTML(titleCase(item.product_title || item.title))}</span>${variant}</div>
        <button type="button" class="sloat-btn sloat-btn--outline sloat-btn--small" data-sloat-open="cart">View cart</button>`;
    } else return;
    el.classList.add('is-visible');
    const hide = () => el.classList.remove('is-visible');
    this.toastTimer = setTimeout(hide, error ? 4200 : 3200);
    el.onpointerenter = () => clearTimeout(this.toastTimer);
    el.onpointerleave = () => (this.toastTimer = setTimeout(hide, 1600));
    el.onclick = (e) => e.target.closest('[data-sloat-open]') && hide();
  },
};
Sloat.cart = Cart;

function escapeHTML(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

/* =============================================================================
   VariantForm
   Reads [data-variants] JSON and the radio groups rendered by
   snippets/sloat-variant-picker.liquid. Keeps the hidden id, price, button,
   availability and labels in step. PDP and the quick-add sheet both use it.
   ========================================================================== */
class VariantForm {
  constructor(form, { onChange } = {}) {
    this.form = form;
    this.scope = form.closest('[data-product-root], [data-quick-sheet-body]') || form;
    const json = $('[data-variants]', form);
    this.variants = json ? JSON.parse(json.textContent) : [];
    this.onChange = onChange;
    this.fieldsets = $$('[data-option-index]', form);
    this.idInput = $('[data-variant-id]', form);
    this.button = $('[data-add-button]', form);
    this.label = $('[data-add-label]', form);
    form.sloatVariantForm = this;
    form.addEventListener('change', (e) => {
      if (!e.target.matches('[data-option-input]')) return;
      const index = Number(e.target.closest('[data-option-index]').dataset.optionIndex);
      this.update(index);
    });
    this.update(null, true);
  }

  selected() {
    return this.fieldsets.map((fs) => {
      const checked = $('[data-option-input]:checked', fs);
      return checked ? checked.value : null;
    });
  }

  match(selected) {
    if (!this.fieldsets.length) return this.variants[0];
    return this.variants.find((v) => v.options.every((o, i) => selected[i] === o));
  }

  get complete() {
    return this.selected().every((v) => v !== null);
  }

  update(changedIndex, initial = false) {
    const sel = this.selected();
    // Availability: a value is available if some in-stock variant has it and
    // agrees with every other chosen option.
    this.fieldsets.forEach((fs) => {
      const i = Number(fs.dataset.optionIndex);
      $$('[data-option-input]', fs).forEach((input) => {
        const ok = this.variants.some((v) => v.available && v.options[i] === input.value && v.options.every((o, j) => j === i || sel[j] === null || sel[j] === o));
        const label = input.closest('label');
        label?.classList.toggle('is-unavailable', !ok);
        $('.sloat-swatch', label || doc.createElement('i'))?.classList.toggle('is-unavailable', !ok);
      });
      const valueLabel = $('[data-option-value]', fs);
      const checked = $('[data-option-input]:checked', fs);
      if (valueLabel && checked) valueLabel.textContent = checked.value;
    });

    const variant = this.complete ? this.match(sel) : null;
    this.variant = variant;
    if (variant && this.idInput) this.idInput.value = variant.id;

    // Price shows the chosen variant, else the first one matching the color.
    const priceFrom = variant || this.variants.find((v) => v.options.every((o, i) => sel[i] === null || sel[i] === o)) || this.variants[0];
    if (priceFrom) {
      $$('[data-price]', this.scope).forEach((el) => {
        const next = Sloat.money(priceFrom.price);
        if (el.textContent.trim() !== next) el.textContent = next;
      });
      $$('[data-compare-price]', this.scope).forEach((el) => {
        el.textContent = priceFrom.compare > priceFrom.price ? Sloat.money(priceFrom.compare) : '';
      });
    }

    if (this.button) {
      const sizeMissing = !this.complete;
      const soldOut = variant && !variant.available;
      this.button.disabled = !!soldOut || (!variant && this.complete);
      this.button.classList.toggle('sloat-btn--muted', sizeMissing);
      const text = soldOut ? 'Sold out' : sizeMissing ? `Select a ${this.missingName()}` : !variant ? 'Unavailable' : `Add to cart · ${Sloat.money(variant.price)}`;
      if (this.label) this.label.textContent = text;
    }
    if (!sel.includes(null)) $$('[data-size-hint]', this.form).forEach((h) => (h.hidden = true));

    if (this.onChange) this.onChange({ variant, selected: sel, changedIndex, initial });
    this.form.dispatchEvent(new CustomEvent('sloat:variant', { bubbles: true, detail: { variant, selected: sel, changedIndex, initial } }));
  }

  missingName() {
    const fs = this.fieldsets.find((f) => !$('[data-option-input]:checked', f));
    const legend = fs && $('.sloat-label', fs);
    return legend ? legend.textContent.trim().toLowerCase() : 'size';
  }

  validate() {
    if (this.complete) return !!(this.variant && this.variant.available);
    const fs = this.fieldsets.find((f) => !$('[data-option-input]:checked', f));
    if (fs) {
      const hint = $('[data-size-hint]', fs);
      if (hint) hint.hidden = false;
      const row = $('.sloat-chips-row, .sloat-swatches', fs);
      row?.classList.remove('sloat-nudge');
      void row?.offsetWidth;
      row?.classList.add('sloat-nudge');
      const rect = fs.getBoundingClientRect();
      if (rect.top < 80 || rect.bottom > innerHeight) fs.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'center' });
      $('[data-option-input]:not(:disabled)', fs)?.focus({ preventScroll: true });
    }
    return false;
  }
}
Sloat.VariantForm = VariantForm;

/* =============================================================================
   Product cards
   ========================================================================== */
const Cards = {
  init() {
    // Swatch preview + select
    doc.addEventListener('pointerover', (e) => {
      if (e.pointerType !== 'mouse') return;
      const sw = e.target.closest('[data-card-swatch]');
      if (sw) this.preview(sw.closest('[data-card]'), sw.dataset.cardSwatch);
    });
    doc.addEventListener('pointerout', (e) => {
      if (e.pointerType !== 'mouse') return;
      const list = e.target.closest('.sloat-card__swatches');
      if (list && !list.contains(e.relatedTarget)) this.restore(list.closest('[data-card]'));
    });
    doc.addEventListener('click', (e) => {
      const sw = e.target.closest('[data-card-swatch]');
      if (sw) {
        e.preventDefault();
        this.select(sw.closest('[data-card]'), sw.dataset.cardSwatch);
        return;
      }
      // Quick add
      const trigger = e.target.closest('[data-quick-trigger]');
      if (trigger) {
        const details = trigger.closest('[data-quick]');
        if (isPhone() || isTouch()) {
          e.preventDefault();
          this.openSheet(details.closest('[data-card]'), trigger);
          return;
        }
        if (details.open) {
          e.preventDefault();
          this.closeQuick(details);
        } else {
          $$('[data-quick][open]').forEach((d) => d !== details && this.closeQuick(d));
        }
        return;
      }
      if (e.target.closest('[data-quick-close]')) {
        e.preventDefault();
        this.closeQuick(e.target.closest('[data-quick]'));
        return;
      }
      // Clicking anywhere else closes open quick panels.
      if (!e.target.closest('[data-quick]')) $$('[data-quick][open]').forEach((d) => this.closeQuick(d));

      // Shared-element page transition from card image to product gallery.
      const link = e.target.closest('[data-card-link]');
      if (link && !e.metaKey && !e.ctrlKey && !e.shiftKey && 'startViewTransition' in doc) {
        $$('[style*="sloat-product-media"]').forEach((el) => (el.style.viewTransitionName = ''));
        const img = $('.sloat-card__img', link.closest('[data-card]'));
        if (img) img.style.viewTransitionName = 'sloat-product-media';
      }
    });
    doc.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') $$('[data-quick][open]').forEach((d) => this.closeQuick(d));
    });
    // Warm the quick-add sheet on touch before the tap completes.
    doc.addEventListener(
      'touchstart',
      (e) => {
        const t = e.target.closest('[data-quick-trigger]');
        if (t) this.loadSheet(this.sheetUrl(t.closest('[data-card]')));
      },
      { passive: true }
    );
    window.addEventListener('pageshow', () => $$('[style*="sloat-product-media"]').forEach((el) => (el.style.viewTransitionName = '')));
  },

  data(card) {
    if (!card) return null;
    if (card._sloat) return card._sloat;
    const json = $('[data-card-data]', card);
    card._sloat = json ? JSON.parse(json.textContent) : null;
    return card._sloat;
  },

  swapImage(card, color) {
    const d = this.data(card);
    const img = $('.sloat-card__img', card);
    const entry = d && d.images && d.images[color];
    if (!img || !entry) return;
    if (img.dataset.color === color) return;
    const pre = new Image();
    pre.sizes = img.sizes;
    pre.srcset = entry.srcset;
    pre.src = entry.src;
    const apply = () => {
      if (!img.dataset.original) {
        img.dataset.original = img.currentSrc || img.src;
        img.dataset.originalSrcset = img.srcset;
      }
      img.srcset = entry.srcset;
      img.src = entry.src;
      img.dataset.color = color;
    };
    (pre.decode ? pre.decode() : Promise.resolve()).then(apply, apply);
  },

  preview(card, color) {
    if (!card) return;
    card.classList.add('is-previewing');
    this.swapImage(card, color);
  },

  restore(card) {
    if (!card) return;
    card.classList.remove('is-previewing');
    const selected = $('[data-card-swatch][aria-pressed="true"]', card);
    if (selected) this.swapImage(card, selected.dataset.cardSwatch);
  },

  select(card, color) {
    const d = this.data(card);
    if (!d) return;
    $$('[data-card-swatch]', card).forEach((b) => {
      const on = b.dataset.cardSwatch === color;
      b.classList.toggle('is-selected', on);
      b.setAttribute('aria-pressed', String(on));
    });
    this.swapImage(card, color);
    const inColor = d.variants.filter((v) => v[1] === color);
    const first = inColor.find((v) => v[3]) || inColor[0];
    if (first) {
      $$('[data-card-link]', card).forEach((a) => {
        const u = new URL(a.href, location.origin);
        u.searchParams.set('variant', first[0]);
        a.href = u.pathname + u.search;
      });
    }
    const chips = $('[data-card-chips] .sloat-chips-row', card);
    if (chips) {
      chips.innerHTML = inColor
        .map(
          (v) =>
            `<button type="submit" name="id" value="${v[0]}" class="sloat-size sloat-size--button sloat-size--compact${v[3] ? '' : ' is-unavailable'}"${v[3] ? '' : ' disabled'} data-variant-id="${v[0]}"><span class="sloat-visually-hidden">Add size </span>${escapeHTML(v[2])}</button>`
        )
        .join('');
    }
  },

  closeQuick(details) {
    if (!details || !details.open) return Promise.resolve();
    if (reducedMotion()) {
      details.open = false;
      return Promise.resolve();
    }
    details.classList.add('is-closing');
    return wait(290).then(() => {
      details.open = false;
      details.classList.remove('is-closing');
    });
  },

  sheetUrl(card) {
    const link = $('[data-card-link]', card);
    const u = new URL(link ? link.href : card.dataset.url, location.origin);
    u.searchParams.set('section_id', 'sloat-quick-add');
    return u.toString();
  },

  sheetCache: new Map(),
  loadSheet(url) {
    if (!this.sheetCache.has(url)) {
      this.sheetCache.set(
        url,
        fetch(url)
          .then((r) => r.text())
          .catch((e) => {
            this.sheetCache.delete(url);
            throw e;
          })
      );
    }
    return this.sheetCache.get(url);
  },

  async openSheet(card, trigger) {
    const dialog = $('[data-sloat-dialog="quick"]');
    if (!dialog) return;
    const slot = $('[data-quick-sheet-slot]', dialog);
    slot.innerHTML = '<div class="sloat-qa-skeleton" aria-hidden="true"><span></span><span></span><span></span><span></span></div>';
    Dialogs.open(dialog, { trigger });
    try {
      const html = await this.loadSheet(this.sheetUrl(card));
      const body = $('[data-quick-sheet-body]', parseHTML(html));
      if (!body) throw new Error('Quick add unavailable');
      slot.replaceChildren(body);
      Sloat.media(body);
      const form = $('form[data-variant-form]', body);
      if (form) new VariantForm(form);
    } catch (e) {
      location.href = card.dataset.url;
    }
  },
};
Sloat.cards = Cards;

/* =============================================================================
   Accordions: smooth height on <details class="sloat-accordion">
   ========================================================================== */
const Accordions = {
  init() {
    doc.addEventListener('click', (e) => {
      const summary = e.target.closest('details.sloat-accordion > summary');
      if (!summary || reducedMotion()) return;
      const details = summary.parentElement;
      const body = $('.sloat-accordion__body', details);
      if (!body || !body.animate) return;
      e.preventDefault();
      if (details._anim) details._anim.cancel();
      if (details.open) {
        const h = body.offsetHeight;
        details.classList.add('is-closing');
        details._anim = body.animate([{ height: `${h}px`, opacity: 1 }, { height: '0px', opacity: 0 }], { duration: 380, easing: 'cubic-bezier(0.45, 0, 0.7, 0.4)' });
        details._anim.onfinish = () => {
          details.open = false;
          details.classList.remove('is-closing');
          details._anim = null;
        };
      } else {
        details.open = true;
        const h = body.offsetHeight;
        details._anim = body.animate([{ height: '0px', opacity: 0 }, { height: `${h}px`, opacity: 1 }], { duration: 560, easing: 'cubic-bezier(0.25, 0.8, 0.25, 1)' });
        details._anim.onfinish = () => (details._anim = null);
      }
    });
  },
};

/* =============================================================================
   Shop the look
   ========================================================================== */
const Looks = {
  init(scope = doc) {
    $$('[data-sloat-look]', scope).forEach((form) => {
      if (form._sloat) return;
      form._sloat = true;
      const label = form.dataset.label || 'Add the look';
      const pieces = () => $$('[data-piece]', form);
      const state = () =>
        pieces().map((el) => {
          const included = !el.classList.contains('is-excluded');
          const fixed = $('[data-fixed]', el);
          const picked = fixed || $('input[type="radio"]:checked', el);
          const anyRadio = $('input[type="radio"]', el);
          const price = picked ? Number(picked.dataset.price) : Number(anyRadio ? anyRadio.dataset.price : 0);
          return { el, included, id: picked ? Number(picked.value) : null, price };
        });
      const btn = $('[data-look-submit]', form);
      const btnLabel = $('.sloat-btn__label', btn) || btn;
      const refresh = () => {
        const all = state();
        const inc = all.filter((p) => p.included);
        const ready = inc.filter((p) => p.id);
        const total = inc.reduce((t, p) => t + p.price, 0);
        all.forEach((p) => {
          const h = $('[data-helper]', p.el);
          if (h) h.hidden = !!p.id;
        });
        $('[data-look-total]', form).textContent = Sloat.money(total);
        const count = $('[data-look-count]', form);
        if (!inc.length) {
          btn.disabled = true;
          btnLabel.textContent = 'Add a piece';
          count.textContent = '0 pieces';
        } else if (ready.length < inc.length) {
          btn.disabled = true;
          btnLabel.textContent = `Choose sizes · ${ready.length} of ${inc.length}`;
          count.textContent = `${ready.length} of ${inc.length} sizes chosen`;
        } else {
          btn.disabled = false;
          btnLabel.textContent = `${label} · ${Sloat.money(total)}`;
          count.textContent = `${inc.length} ${inc.length === 1 ? 'piece' : 'pieces'}`;
        }
      };
      form.addEventListener('change', refresh);
      form.addEventListener('click', (e) => {
        const t = e.target.closest('[data-toggle]');
        if (!t) return;
        const el = t.closest('[data-piece]');
        const excluded = el.classList.toggle('is-excluded');
        t.textContent = excluded ? 'Add back' : 'Remove';
        t.setAttribute('aria-pressed', String(!excluded));
        refresh();
      });
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const err = $('[data-look-error]', form);
        err.hidden = true;
        const items = state()
          .filter((p) => p.included && p.id)
          .map(({ id }) => ({ id, quantity: 1 }));
        if (!items.length) return;
        Cart.setButton(btn, 'loading');
        try {
          await Cart.addItems(items);
          Cart.setButton(btn, 'done');
          await wait(360);
          Cart.open();
        } catch (error) {
          err.textContent = error.message;
          err.hidden = false;
        } finally {
          setTimeout(() => Cart.setButton(btn, 'idle'), 1200);
        }
      });
      // Hotspots: highlight the matching piece.
      $$('[data-hotspot]', form.closest('.sloat-look') || doc).forEach((spot) => {
        spot.addEventListener('click', (e) => {
          const target = doc.getElementById(spot.getAttribute('href').slice(1));
          if (!target) return;
          e.preventDefault();
          target.classList.remove('is-flash');
          void target.offsetWidth;
          target.classList.add('is-flash');
          if (isPhone()) target.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'center' });
        });
      });
      refresh();
    });
  },
};
Sloat.looks = Looks;

/* =============================================================================
   Motion: reveal on scroll + image fade-in
   ========================================================================== */
let revealObserver;
Sloat.reveal = (scope = doc) => {
  const items = $$('[data-reveal]:not(.is-in)', scope);
  if (!items.length) return;
  if (reducedMotion() || !('IntersectionObserver' in window)) {
    items.forEach((el) => el.classList.add('is-in', 'is-instant'));
    return;
  }
  if (!revealObserver) {
    revealObserver = new IntersectionObserver(
      (entries) => {
        let n = 0;
        entries
          .filter((en) => en.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top || a.boundingClientRect.left - b.boundingClientRect.left)
          .forEach((en) => {
            const el = en.target;
            if (!el.style.getPropertyValue('--i')) el.style.setProperty('--i', String(Math.min(n++, 8)));
            el.classList.add('is-in');
            revealObserver.unobserve(el);
          });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.01 }
    );
  }
  const vh = innerHeight;
  items.forEach((el) => {
    const r = el.getBoundingClientRect();
    if (r.top < vh * 0.98 && r.bottom > 0 && scope === doc && !Sloat._revealReady) el.classList.add('is-in', 'is-instant');
    else revealObserver.observe(el);
  });
};

Sloat.media = (scope = doc) => {
  $$('.sloat-media img', scope).forEach((img) => {
    if (img.complete && img.naturalWidth) img.classList.add('is-loaded');
  });
};

/* =============================================================================
   Carousel: native scroll-snap track, arrows step one item, states follow scroll.
   Markup: [data-carousel] > [data-carousel-track] + [data-carousel-prev]/[data-carousel-next]
   ========================================================================== */
const Carousels = {
  init(scope = doc) {
    $$('[data-carousel]', scope).forEach((el) => {
      if (el._carousel) return;
      el._carousel = true;
      const track = $('[data-carousel-track]', el);
      const prev = $('[data-carousel-prev]', el);
      const next = $('[data-carousel-next]', el);
      if (!track) return;
      const step = () => {
        const items = track.children;
        if (items.length < 2) return track.clientWidth;
        return items[1].getBoundingClientRect().left - items[0].getBoundingClientRect().left;
      };
      const update = () => {
        const max = track.scrollWidth - track.clientWidth - 2;
        el.classList.toggle('is-static', max <= 0);   // everything fits: no arrows
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= max;
      };
      const go = (dir) => track.scrollBy({ left: dir * step(), behavior: reducedMotion() ? 'auto' : 'smooth' });
      prev?.addEventListener('click', () => go(-1));
      next?.addEventListener('click', () => go(1));
      let raf;
      track.addEventListener('scroll', () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(update); }, { passive: true });
      window.addEventListener('resize', update, { passive: true });
      track.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); go(1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); go(-1); }
      });
      update();
    });
  },
};
Sloat.carousels = Carousels;

/* =============================================================================
   Product recommendations (Wear it with fallback)
   ========================================================================== */
Sloat.recommendations = (scope = doc) => {
  $$('[data-recommendations]', scope).forEach(async (el) => {
    if (el._loading) return;
    el._loading = true;
    try {
      const res = await fetch(el.dataset.recommendations);
      const fresh = $('.sloat-rail', parseHTML(await res.text()));
      if (!fresh || !$('[data-card]', fresh)) {
        el.closest('.shopify-section')?.setAttribute('hidden', '');
        return;
      }
      el.replaceWith(fresh);
      Sloat.media(fresh);
      Carousels.init(fresh.parentNode || doc);
      Sloat.reveal(fresh);
    } catch (e) {
      el.closest('.shopify-section')?.setAttribute('hidden', '');
    }
  });
};

/* =============================================================================
   Boot
   ========================================================================== */
function boot() {
  doc.addEventListener('load', (e) => e.target.tagName === 'IMG' && e.target.classList.add('is-loaded'), true);
  Sloat.media();
  Dialogs.init();
  Header.init();
  Search.init();
  Cart.init();
  Cards.init();
  Accordions.init();
  Looks.init();
  Carousels.init();
  Sloat.recommendations();
  Sloat.reveal();
  Sloat._revealReady = true;
  $$('form[data-variant-form]:not([data-product-form])').forEach((f) => new VariantForm(f));

  // Theme editor support
  doc.addEventListener('shopify:section:load', (e) => {
    Sloat.media(e.target);
    Looks.init(e.target);
    Carousels.init(e.target);
    Sloat.reveal(e.target);
    $$('[data-reveal]', e.target).forEach((el) => el.classList.add('is-in', 'is-instant'));
    if (e.target.querySelector('[data-sloat-header]')) Header.init();
  });

  Sloat.ready = true;
  doc.dispatchEvent(new CustomEvent('sloat:ready'));
}

if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', boot, { once: true });
else boot();
